import React from 'react';

import { ActionIcon, Paper, Skeleton, Text, Title, Tooltip, createStyles } from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { IconPrinter } from '@tabler/icons-react';
import PayrollPeriodSelect from 'components/form-fields/select-fields/PayrollPeriodSelect';
import { useLocalization } from 'context/localization-context';
import useCreatePayrollPeriod from 'hooks/payrollPeriod/useCreatePayrollPeriod';
import useUpdatePayrollPeriod from 'hooks/payrollPeriod/useUpdatePayrollPeriod';
import { PayrollEventRetrieve } from 'types';

import ModalBulkPayrollExport from '../../PayrollReport/components/ModalBulkPayrollExport';

const useStyles = createStyles(theme => ({
  header: {
    background: theme.colors.gray[0],
    padding: theme.spacing.xl,
    paddingBottom: theme.spacing.xl,
    display: 'grid',
    borderBottom: `1px solid ${theme.colors.gray[3]}`,
    borderRadius: 0,
    gridTemplateColumns: '1fr 1fr max-content min-content',
    gridTemplateRows: 'auto',
    gridTemplateAreas: `"title total period download"`,
    alignItems: 'center',
    gap: theme.spacing.md,
    [theme.fn.smallerThan('lg')]: {
      gridTemplateColumns: '1fr 1fr max-content min-content',
      gridTemplateAreas: `
      "title . . download"
      "total . period period"
      `,
    },
    [theme.fn.smallerThan('md')]: {
      gridTemplateColumns: '1fr 1fr min-content min-content',
      gridTemplateAreas: `
        'title title title download'
        'total total info info'
        'period period period period'
        `,
    },
  },
  title: {
    gridArea: 'title',
    whiteSpace: 'nowrap',
  },
  total: {
    gridArea: 'total',
    whiteSpace: 'nowrap',
  },
  period: {
    gridArea: 'period',
    minWidth: '20rem',
  },
  download: {
    gridArea: 'download',
  },
}));

const PayrollReportPageHeader = ({
  staffName,
  staffId,
  payrollPeriod,
  setPayrollPeriod,
  tableData,
  isSuccess,
  isError,
  isLoading,
}: {
  staffName?: string;
  staffId?: string;
  permission?: boolean;
  payrollPeriod: string | null;
  setPayrollPeriod: React.Dispatch<React.SetStateAction<string | null>>;
  tableData: PayrollEventRetrieve[];
  isSuccess: boolean;
  isError: boolean;
  isLoading: boolean;
}): JSX.Element => {
  const { classes } = useStyles();
  const { formatCurrency } = useLocalization();
  const [isBulkExportModalOpened, { open: openBulkExportModal, close: closeBulkExportModal }] =
    useDisclosure(false);

  const updateMutation = useUpdatePayrollPeriod();
  const createMutation = useCreatePayrollPeriod();

  const getPayrollSummary = (): JSX.Element => {
    if (isLoading) {
      return (
        <div className={classes.total}>
          <Skeleton height='2rem' width='5rem' />
        </div>
      );
    }
    if (isError) {
      return (
        <div className={classes.total}>
          <Title order={1}>Error</Title>
        </div>
      );
    }
    if (isSuccess) {
      const formattedTotalPayout = formatCurrency(
        tableData.reduce((sum, obj) => sum + obj.payoutAmount, 0) / 100,
      );

      return (
        <>
          <div className={classes.total}>
            <Text color='dimmed' fw='bold' size='1.5rem'>
              {`Total Payout: ${formattedTotalPayout}`}
            </Text>
          </div>
        </>
      );
    }
    return <></>;
  };

  return (
    <>
      <Paper className={classes.header}>
        <Title order={1} className={classes.title} pr='2rem'>
          Payroll Report - {staffName}
        </Title>
        {getPayrollSummary()}

        <PayrollPeriodSelect
          data-cy='payrollPeriodSelect'
          className={classes.period}
          value={payrollPeriod}
          onChange={setPayrollPeriod}
          disabled={updateMutation.isLoading || createMutation.isLoading}
        />

        <Tooltip label='Export payroll report' className={classes.download}>
          <ActionIcon onClick={openBulkExportModal} data-cy='coachBulkPayrollExportButton'>
            <IconPrinter />
          </ActionIcon>
        </Tooltip>
      </Paper>

      <ModalBulkPayrollExport
        opened={isBulkExportModalOpened}
        handleClose={closeBulkExportModal}
        staffId={staffId}
        staffName={staffName}
      />
    </>
  );
};

export default PayrollReportPageHeader;
